import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import * as backend from '@/api/backend';
import { checkResponseStatus } from '@/util/check';
import { ConnectionRefusedError, UnexpectedResponseStatusError } from '@/util/error';
import { notify } from '@/util/notifier';

const defaultState = {
  initialized: false,
  email: '',
  photoURL: 'https://lh3.googleusercontent.com/-r3ejcQ-K-8w/AAAAAAAAAAI/AAAAAAAAAAA/ACHi3rd1G7EuD_v3F4r2i8gJ6sOC1qltjA/photo.jpg',
  idToken: '',
  uid: '',
  emailVerified: false,
  authorized: false,
  authenticated: false,
  loading: false,
  error: false,
  role: null,
};

const mutations = {
  initialized(state) {
    state.initialized = true;
  },
  fetchLogin(state) {
    state.loading = true;
    state.error = false;
  },
  errorLogin(state) {
    state.loading = false;
    state.error = true;
  },
  successLogin(state, {
    email,
    photoURL,
    idToken,
    emailVerified,
    authorized,
    uid,
    role,
  }) {
    state.emailVerified = emailVerified;
    state.authorized = authorized;
    state.authenticated = true;
    state.email = email;
    state.photoURL = photoURL;
    state.idToken = idToken;
    state.uid = uid;
    state.loading = false;
    state.error = false;
    state.role = role;
  },
  partialLogin(state, {
    email,
    photoURL,
    idToken,
    emailVerified,
    authorized,
    uid,
    role,
  }) {
    state.emailVerified = emailVerified;
    state.authorized = authorized;
    state.authenticated = true;
    state.email = email;
    state.photoURL = photoURL;
    state.idToken = idToken;
    state.uid = uid;
    state.loading = false;
    state.error = false;
    state.role = role;
  },
  logout(state) {
    state.email = '';
    state.photoURL = '';
    state.idToken = '';
    state.uid = '';
    state.authenticated = false;
    state.emailVerified = false;
    state.authorized = false;
    state.role = null;
    state.loading = false;
    state.error = false;
  },
};

const fetchAccount = async () => {
  const response = await backend.getAccount();
  await checkResponseStatus(200, response);
  return response.data;
};

const getRole = (account) => {
  if (account.roles.includes('admin')) {
    return 'admin';
  } if (account.roles.includes('moderator')) {
    return 'moderator';
  }
  return null;
};

const getErrorCode = (response) => {
  if (!response?.data) return null;
  if (typeof response.data === 'object') {
    return response.data.error?.code
      || response.data.code
      || null;
  }
  return null;
};

const buildPartialUser = async (emailVerifiedOverride) => {
  const {
    email,
    photoURL,
    emailVerified,
    uid,
  } = firebase.auth().currentUser;
  const idToken = await firebase.auth().currentUser.getIdToken();

  return {
    email,
    photoURL,
    idToken,
    emailVerified: emailVerifiedOverride ?? emailVerified,
    uid,
    authorized: false,
    role: null,
  };
};

const actions = {
  async updateUser({ commit, dispatch }, auth) {
    if (auth && Array.isArray(auth.providerData)
      && !auth.providerData.some((p) => p.providerId === 'password')) {
      firebase.auth().signOut();
    }
    if (auth) {
      try {
        commit('fetchLogin');

        const account = await fetchAccount();
        const role = getRole(account);
        const {
          email,
          photoURL,
          emailVerified,
          uid,
        } = firebase.auth().currentUser;
        const idToken = await firebase.auth()
          .currentUser
          .getIdToken();

        const isTestEmail = email && (
          email.endsWith('@ollaa.app')
        ) && process.env.NODE_ENV !== 'production';
        const user = {
          email,
          photoURL,
          idToken,
          emailVerified,
          uid,
          authorized: (emailVerified || isTestEmail) && role != null,
          role,
        };
        commit('successLogin', user);
      } catch (e) {
        if (e instanceof ConnectionRefusedError) {
          commit('errorLogin');
          // Offline banner in header is enough; avoid toast spam on token refresh / nodemon restarts.
        } else if (e instanceof UnexpectedResponseStatusError && e.response?.status === 401) {
          const errorCode = getErrorCode(e.response);

          if (errorCode === 'email_not_verified') {
            const user = await buildPartialUser(false);
            commit('partialLogin', user);
            notify(
              'Bitte bestätige deine E-Mail-Adresse. Prüfe auch den Spam-Ordner.',
              'warning',
            );
          } else if (errorCode === 'not_authorized') {
            const user = await buildPartialUser();
            commit('partialLogin', user);
          } else {
            dispatch('logout');
            notify('Anmeldung fehlgeschlagen. Bitte erneut versuchen.', 'danger');
          }
        } else {
          console.error(e);
          commit('errorLogin');
        }
      }
    } else {
      commit('logout');
    }
    commit('initialized');
  },
  logout() {
    firebase.auth().signOut().catch(console.error);
  },
};

export default {
  namespaced: true,
  state: defaultState,
  mutations,
  actions,
};
